import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { environment } from '../environments/environment';
import { Title } from '@angular/platform-browser';
import { GlobalConfigService } from './_core/services/global-config.service';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { distinctUntilChanged, filter, map, shareReplay, Subject, takeUntil } from 'rxjs';
import { EntityDetailsRoutes, EntityDetailsTabRoutes } from './_core/constants/Global/AnalyticsTracking';
import { GoogleAnalyticsService } from './_core/api/google-analytics.service';
import { Meta } from '@angular/platform-browser';
import Urls from './_core/constants/Urls';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  favIcon: HTMLLinkElement;
  private readonly destroyed$ = new Subject<void>();

  // prettier-ignore
  constructor(
    private titleService: Title,
    private globalConfigService: GlobalConfigService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private analyticsService: GoogleAnalyticsService,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.favIcon = this.document.querySelector('#favicon');
    this.setEnvironmentConfiguration();
    this.setColorTheme();
  }

  ngOnInit(): void {
    this.computePageTitle();
    if (this.globalConfigService.globalConfig.enableGoogleAnalytics && this.globalConfigService.globalConfig.googleAnalyticsTrackingCode.length > 0) {
      this.analyticsService.initialize();
    }
    if (!environment.production) {
      this.meta.addTag({
        name: 'robots',
        content: 'noindex, nofollow',
      });
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  setEnvironmentConfiguration(): void {
    this.favIcon.href = environment.favicon;
  }

  setColorTheme(): void {
    const colors = [
      { name: 'auth-primary', value: `#${this.globalConfigService.globalConfig.authButtonBackgroundColor.hex}` },
      { name: 'auth-btn-text', value: `#${this.globalConfigService.globalConfig.authButtonTextColor.hex}` },
      { name: 'auth-secondary', value: `#${this.globalConfigService.globalConfig.authTextColor.hex}` },
    ];
    colors.forEach((color) => this.document.documentElement.style.setProperty(`--${color.name}`, color.value));
  }

  computePageTitle(): void {
    const navEnd$ = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const title$ = navEnd$.pipe(
      map(() => this.getDeepestChild(this.activatedRoute)),
      map((route) => {
        const routeTitle = route?.snapshot?.data?.['title'];
        // Support: string or function returning string (handy for dynamic titles)
        const resolved = typeof routeTitle === 'function' ? routeTitle(route) : routeTitle;
        return resolved || this.globalConfigService.globalConfig.applicationTitle;
      }),
      distinctUntilChanged()
    );

    navEnd$
      .pipe(
        // pair the latest nav event with the latest resolved title
        // without nested subs:
        //   navEnd$ drives the stream, title$ is replayed
        map((ev) => ({ ev })),
        // merge with the latest computed title
        // (shareReplay on navEnd$ + pure maps make this cheap)
        // micro-optimization: inline latest title via a synchronous read
        // by briefly subscribing via switchMap is overkill; use combineLatest-like mapping:
        // we'll just subscribe once below and compute title there.
        takeUntil(this.destroyed$)
      )
      .subscribe(({ ev }) => {
        const title = this.resolveTitleFromTree();
        if (!this.isDetailsOrDetailsTab(ev.urlAfterRedirects)) {
          this.titleService.setTitle(title);
        }

        // Track page view for Google Analytics with a small delay to ensure user properties are set
        if (
          this.globalConfigService.globalConfig?.enableGoogleAnalytics &&
          this.globalConfigService.globalConfig?.googleAnalyticsTrackingCode.length > 0 &&
          this.shouldTrack(new URL(window.location.href))
        ) {
          // Small delay to ensure user properties are properly initialized
          setTimeout(() => {
            this.analyticsService.trackPageView(ev.urlAfterRedirects);
          }, 100);
        }
      });

    // Pre-warm the title stream so resolveTitleFromTree() has the latest value
    title$.pipe(takeUntil(this.destroyed$)).subscribe();
  }

  /** Grabs the deepest child ActivatedRoute */
  private getDeepestChild(route: ActivatedRoute): ActivatedRoute {
    let child = route;
    while (child.firstChild) child = child.firstChild;
    return child;
  }

  /** Computes the current title synchronously from the route tree */
  private resolveTitleFromTree(): string {
    const deepest = this.getDeepestChild(this.activatedRoute);
    const routeTitle = deepest?.snapshot?.data?.['title'];
    const resolved = typeof routeTitle === 'function' ? routeTitle(deepest) : routeTitle;
    return resolved || this.globalConfigService.globalConfig.applicationTitle;
  }

  /** True if URL matches any details or details-tab route */
  private isDetailsOrDetailsTab(url: string): boolean {
    return this.matchesAny(url, EntityDetailsRoutes) && this.matchesAny(url, EntityDetailsTabRoutes);
  }

  /** Supports string or RegExp patterns in your route lists */
  private matchesAny(url: string, patterns: Array<string | RegExp>): boolean {
    return patterns.some((p) => (typeof p === 'string' ? url.includes(p) : p.test(url)));
  }

  private shouldTrack(url: URL): boolean {
    // Block localhost entirely
    // if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') return false;

    // Block a specific route + issuer combo (your example)
    // iss is URL-encoded; decode before comparing
    const iss = url.searchParams.get('iss');
    const decodedIss = iss ? decodeURIComponent(iss) : '';
    const isDevIssuer = decodedIss.startsWith(environment.authConfig.issuer);

    if (url.pathname === `/${Urls.APP}/${Urls.HOMEPAGE}` && isDevIssuer) return false;

    return true; // ✅ all others can be tracked
  }
}
